import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Banner from '../../components/Banner';
import LegalLinks from '../../components/LegalLinks';
import AnonymousLimitDialog from '../../components/AnonymousLimitDialog';
import LimitReachedDialog from '../../components/LimitReachedDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useMessage } from '../../components/MessageProvider';
import { getAnonymousSession } from '../../lib/anonymousSession';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import type { CreateDocumentRequest, DocumentSummary } from '../../lib/apiClient';
import { trackEvent } from '../../lib/analytics';

export default function Documents() {
  const navigate = useNavigate();
  const { showError } = useMessage();
  const { createDocument, deleteDocument, getDocuments } = useApi();
  const { user = null, loading: authLoading } = useAuth() || {};
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true); // Start true to show spinner on initial load
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anonymousLimitDialogOpen, setAnonymousLimitDialogOpen] = useState(false);
  const [planLimitDialogOpen, setPlanLimitDialogOpen] = useState(false);
  const [planLimitMessage, setPlanLimitMessage] = useState('');
  const [documentToDelete, setDocumentToDelete] = useState<DocumentSummary | null>(null);
  const fetchingRef = React.useRef(false); // Prevent concurrent requests
  
  // Memoize session so it doesn't change on every render
  const session = useMemo(() => getAnonymousSession(), []);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }
    
    // Prevent concurrent fetches
    if (fetchingRef.current) {
      return;
    }
    
    // If no session AND not authenticated, redirect to home
    if (!session && !user) {
      navigate('/');
      return;
    }

    // Fetch user's documents
    // Note: We pass the account_id from local storage (if available), 
    // but the server will use the authenticated user's account_id if they are logged in (more secure)
    const fetchDocuments = async () => {
      // Set fetching flag
      fetchingRef.current = true;
      
      try {
        setLoading(true);
        // For authenticated users without session, pass a dummy value - server will override
        const accountIdParam = session?.accountId || '00000000-0000-0000-0000-000000000000';

        const result = await getDocuments(
          {
            account_id: accountIdParam,
            anonymous_user_id: user && session?.userId ? session.userId : undefined,
            anonymous_account_id: user && session?.accountId ? session.accountId : undefined,
          },
          {
            shouldShowError: (error) =>
              error.code !== 'ACCOUNT_NOT_FOUND' &&
              error.code !== 'AUTHENTICATION_REQUIRED',
          }
        );

        if ('error' in result) {
          if (result.error.code === 'ACCOUNT_NOT_FOUND') {
            const { clearAnonymousSession } = await import('../../lib/anonymousSession');
            clearAnonymousSession();

            if (user) {
              // Avoid a self-navigation loop on '/' for authenticated users.
              setDocuments([]);
              showError('Unable to load documents for this account. Please create a new document.');
              return;
            }

            navigate('/');
            return;
          }

          if (result.error.code === 'AUTHENTICATION_REQUIRED') {
            const { clearAnonymousSession } = await import('../../lib/anonymousSession');
            clearAnonymousSession();
            showError('This account requires authentication. Please log in again.');
            navigate('/auth?mode=signin');
            return;
          }

          return;
        }

        const data = result.data;
        setDocuments(data.documents || []);
        
        // If the server indicates the account was linked, clear local storage
        if (data.account_was_linked) {
          const { clearAnonymousSession } = await import('../../lib/anonymousSession');
          clearAnonymousSession();
        }
      } catch (err) {
        showError(err.message || 'Failed to load documents');
        console.error('Error fetching documents:', err);
      } finally {
        setLoading(false);
        fetchingRef.current = false; // Reset flag
      }
    };

    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]); // Re-fetch when auth state changes

  const handleCreateDocument = async () => {
    try {
      const requestBody: CreateDocumentRequest = {
        account_id: session?.accountId || '00000000-0000-0000-0000-000000000000',
        user_id: session?.userId || '00000000-0000-0000-0000-000000000000',
      };
      
      // If user is authenticated AND we have an anonymous session, pass it for linking
      if (user && session?.userId && session?.accountId) {
        requestBody.anonymous_user_id = session.userId;
        requestBody.anonymous_account_id = session.accountId;
      }

      const result = await createDocument(requestBody, {
        shouldShowError: (error) =>
          error.code !== 'ANONYMOUS_DOCUMENT_LIMIT_REACHED' &&
          error.code !== 'SIGNED_UP_DOCUMENT_LIMIT_REACHED',
      });
      if ('error' in result) {
        if (result.error.code === 'ANONYMOUS_DOCUMENT_LIMIT_REACHED') {
          setAnonymousLimitDialogOpen(true);
        } else if (result.error.code === 'SIGNED_UP_DOCUMENT_LIMIT_REACHED') {
          setPlanLimitMessage(result.error.message);
          setPlanLimitDialogOpen(true);
        }
        return;
      }

      const data = result.data;
      navigate(`/sheet/${data.id}`);
    } catch (err) {
      showError(err.message || 'Failed to create document');
    }
  };

  const handleDocumentClick = (docId) => {
    trackEvent('view_document', { document_id: docId });
    navigate(`/sheet/${docId}`);
  };

  const getDocumentDisplayTitle = (doc: DocumentSummary): string => doc.title?.trim() || 'Untitled';

  const handleDeleteClick = (e, doc) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await deleteDocument(documentToDelete.id);
      if ('error' in result) {
        setDeleteDialogOpen(false);
        setDocumentToDelete(null);
        return;
      }
      
      // Remove document from list
      setDocuments(documents.filter(doc => doc.id !== documentToDelete.id));
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch (err) {
      showError(err.message || 'Failed to delete document');
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const handleAnonymousLimitDialogClose = () => {
    setAnonymousLimitDialogOpen(false);
  };

  const handleAnonymousLimitSignUp = () => {
    setAnonymousLimitDialogOpen(false);
    navigate('/auth?mode=signup');
  };

  const handlePlanLimitDialogClose = () => {
    setPlanLimitDialogOpen(false);
    setPlanLimitMessage('');
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: (theme) => (theme.palette.mode === 'light' ? '#f4f6f7' : theme.palette.background.default) }}>
        <Banner />
        <Container maxWidth="lg" sx={{ pt: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: (theme) => (theme.palette.mode === 'light' ? '#f4f6f7' : theme.palette.background.default) }}>
      <Banner />
      <Container maxWidth="lg" sx={{ pt: 10 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            My Documents
          </Typography>
          {documents.length > 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateDocument}
              sx={{ textTransform: 'none' }}
            >
              New Document
            </Button>
          )}
        </Box>

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CircularProgress />
          </Paper>
        ) : documents.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="text.secondary">
              No documents yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first T-account document to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateDocument}
              sx={{ textTransform: 'none' }}
            >
              Create Document
            </Button>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
            <List disablePadding>
              {documents.map((doc, index) => (
                <ListItem
                  key={doc.id}
                  disablePadding
                  sx={{ mb: index < documents.length - 1 ? 1 : 0 }}
                  secondaryAction={
                    <IconButton 
                      edge="end" 
                      aria-label="delete"
                      onClick={(e) => handleDeleteClick(e, doc)}
                      sx={{ 
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '.MuiListItem-root:hover &': {
                          opacity: 1
                        }
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                >
                  <ListItemButton 
                    onClick={() => handleDocumentClick(doc.id)}
                    sx={{ 
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemText
                      primary={getDocumentDisplayTitle(doc)}
                      secondary={`Last updated ${new Date(doc.updated_at).toLocaleDateString()}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Document"
          message={`Are you sure you want to delete ${documentToDelete ? getDocumentDisplayTitle(documentToDelete) : 'this document'}?`}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />

        <AnonymousLimitDialog
          open={anonymousLimitDialogOpen}
          onClose={handleAnonymousLimitDialogClose}
          onSignUp={handleAnonymousLimitSignUp}
        />

        <LimitReachedDialog
          open={planLimitDialogOpen}
          title="Plan Limit Reached"
          message={planLimitMessage}
          onClose={handlePlanLimitDialogClose}
        />

        <Box sx={{ mt: 3.5, mb: 2, color: 'text.secondary' }}>
          <LegalLinks centered />
        </Box>
      </Container>
    </Box>
  );
}
